import * as React from "react";
import { 
    List,
    Datagrid, Filter, TopToolbar, SortButton, ExportButton,
    TextField, NumberField, FunctionField, 
    ReferenceInput, SelectInput, RadioButtonGroupInput,
    useListSortContext,
} from 'react-admin';
import { TableHead, TableRow, TableCell, useMediaQuery, makeStyles } from '@material-ui/core';
import Config from "../../providers/configProvider";
import LogoField from '../../components/LogoField';

const ListActions = ({ sortFields }) => (
    <TopToolbar>
        <SortButton fields={sortFields} />
        <ExportButton />
    </TopToolbar>
);

const useStyles = makeStyles({
    cellSmall: {
        padding: '8px'
    },
    cell: {
        textAlign: 'right'
    },
    cellLeft: {
        textAlign: 'left'
    },
    cellBorder: {
        borderLeft: '1px solid #000',
        textAlign: 'right'
    },
    cellBorderLeft: {
        borderLeft: '1px solid #000',
        textAlign: 'left'
    },
    cellBorderDouble: {
        borderLeft: '3px double #000',
        textAlign: 'right'
    },
    topHeader: {
        fontWeight: 'bold',
        textAlign: 'center'
    },
    topHeaderBorder: {
        fontWeight: 'bold',
        textAlign: 'center',
        borderLeft: '1px solid #000'
    },
    topHeaderBorderDouble: {
        fontWeight: 'bold',
        textAlign: 'center',
        borderLeft: '3px double #000'
    },
    green: {
        fontWeight: 'bold',
        color: 'green'
    },
    greenSmall: {
        color: 'green'
    },
    red: {
        fontWeight: 'bold',
        color: 'red'
    },
    redSmall: {
        color: 'red'
    }
});

/*** TEAM STATS HISTORY ***/
const teamStatsSortFields = [
    { field: 'season', label: 'season' }, 
    { field: 'points.for', label: 'points for' }, 
    { field: 'points.against', label: 'points against' }, 
    { field: 'points.diff', label: 'points diff' }, 
    { field: 'tries.for', label: 'tries for' }, 
    { field: 'tries.against', label: 'tries against' }, 
    { field: 'tries.diff', label: 'tries diff' }, 
    { field: 'goals.for', label: 'goals for' }, 
    { field: 'goals.against', label: 'goals against' }, 
    { field: 'goals.diff', label: 'goals diff' }, 
    { field: 'fieldGoals1.for', label: '1 point field goals for' }, 
    { field: 'fieldGoals1.against', label: '1 point field goals against' }, 
    { field: 'fieldGoals1.diff', label: '1 point field goals diff' }, 
    { field: 'fieldGoals2.for', label: '2 point field goals for' }, 
    { field: 'fieldGoals2.against', label: '2 point field goals against' }, 
    { field: 'fieldGoals2.diff', label: '2 point field goals diff' }, 
    { field: 'sinBins.for', label: 'sin bins for' }, 
    { field: 'sinBins.against', label: 'sin bins against' }, 
    { field: 'sinBins.diff', label: 'sin bins diff' }, 
    { field: 'sendOffs.for', label: 'send offs for' }, 
    { field: 'sendOffs.against', label: 'send offs against' }, 
    { field: 'sendOffs.diff', label: 'send offs diff' }
];

const TeamStatHeader = ({ children }) => {
    const classes = useStyles();
    const { currentSort } = useListSortContext();
    return (
        <TableHead>
            <TableRow>
                <TableCell colspan={2} />
                <TableCell colSpan={3} className={classes.topHeader}>Points</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorder}>Tries</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorder}>Goals</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorder}>1 Point Field Goals</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorder}>2 Point Field Goals</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorder}>Sin Bins</TableCell>
                <TableCell colSpan={3} className={classes.topHeaderBorder}>Send Offs</TableCell>
            </TableRow>
            <TableRow>
                {React.Children.map(children, child => (
                    <TableCell key={child.props.source} className={child.props.source && child.props.source !== 'points.for' && child.props.source.endsWith('.for') ? classes.cellBorder : (child.props.label === 'Competition' || child.props.label === 'Season' ? classes.cellLeft : classes.cell)}>
                        {child.props.label}{teamStatsSortFields.find(f => f.field === child.props.source) && currentSort.field === teamStatsSortFields.find(f => f.field === child.props.source).label
                            ? (currentSort.order === 'DESC' ? <span> &darr;</span> : <span> &uarr;</span>)
                            :  null}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
};

export const TeamStatHistoryList = props => {
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const classes = useStyles();

    const TeamStatFilter = props => (
        <Filter {...props}>
            <ReferenceInput label="Team" source="teamId" reference="team" perPage={1000} alwaysOn>
                <SelectInput optionText={<LogoField type="teams" source="code" text="name" />} />
            </ReferenceInput>
            <RadioButtonGroupInput label="Display" source="showAverages" choices={[
                { id: false, name: 'Totals' },
                { id: true, name: 'Averages' }
            ]} alwaysOn />
        </Filter>
    );

    const StatField = (props) => <FunctionField {...props} render={record => <NumberField {...props} options={{ minimumFractionDigits: record.showAverages ? 1 : 0 }} />} />;

    return (
        <List {...props} 
            bulkActionButtons={false} 
            filters={<TeamStatFilter />}
            filterDefaultValues={{ teamId: Config.MyTeamId, showAverages: false }}
            actions={<ListActions sortFields={teamStatsSortFields.map(f => f.label)} />}
            sort={{ field: 'season', order: 'DESC' }}
            pagination={null}
        >
            {isSmall ? (
                <Datagrid
                    classes={{ rowCell: classes.cellSmall, headerCell: classes.cellSmall }}
                    rowClick={(id, basePath, record) => `/stat/teams/${record.id};${record.teamId}/show`}
                >
                    <LogoField type="competitions" source="competitionCode" text="competitionCode" label="Competition" />
                    <TextField source="seasonYear" label="Season" />
                    <StatField source="points.for" label="Pts" />                
                    <StatField source="tries.for" label="T" />
                    <StatField source="goals.for" label="G" />
                    <StatField source="fieldGoals1.for" label="FG1" />
                    <StatField source="fieldGoals2.for" label="FG2" />
                    <StatField source="sinBins.for" label="SB" />
                    <StatField source="sendOffs.for" label="SO" />
                </Datagrid>
            ) : (
                <Datagrid
                    header={<TeamStatHeader />}
                    classes={{ rowCell: classes.cell, headerCell: classes.cell }}
                    rowClick={(id, basePath, record) => `/stat/teams/${record.id};${record.teamId}/show`}
                >
                    <LogoField type="competitions" source="competitionCode" text="competitionCode" label="Competition" cellClassName={classes.cellLeft} />
                    <TextField source="seasonYear" label="Season" cellClassName={classes.cellLeft} />
                    <StatField source="points.for" label="F" />
                    <StatField source="points.against" label="A" />
                    <StatField source="points.diff" label="+/-" />
                    <StatField source="tries.for" label="F" cellClassName={classes.cellBorder} />
                    <StatField source="tries.against" label="A" />
                    <StatField source="tries.diff" label="+/-" />
                    <StatField source="goals.for" label="F" cellClassName={classes.cellBorder} />
                    <StatField source="goals.against" label="A" />
                    <StatField source="goals.diff" label="+/-" />
                    <StatField source="fieldGoals1.for" label="F" cellClassName={classes.cellBorder} />
                    <StatField source="fieldGoals1.against" label="A" />
                    <StatField source="fieldGoals1.diff" label="+/-" />
                    <StatField source="fieldGoals2.for" label="F" cellClassName={classes.cellBorder} />
                    <StatField source="fieldGoals2.against" label="A" />
                    <StatField source="fieldGoals2.diff" label="+/-" />
                    <StatField source="sinBins.for" label="F" cellClassName={classes.cellBorder} />
                    <StatField source="sinBins.against" label="A" />
                    <StatField source="sinBins.diff" label="+/-" />
                    <StatField source="sendOffs.for" label="F" cellClassName={classes.cellBorder} />
                    <StatField source="sendOffs.against" label="A" />
                    <StatField source="sendOffs.diff" label="+/-" />
                </Datagrid>
            )}
        </List>
    );
};
